import React, { useState, useEffect } from 'react';
import {
  getUsers, createUser, updateUser, deleteUser,
  getCurrencies, updateCurrency, getCurrencyLogs,
  getInsurance, createInsurance, updateInsurance, deleteInsurance,
  getAllConfigs, updateConfig,
  getAllCustomBurdens, createCustomBurden, updateCustomBurden, deleteCustomBurden,
  calculate,
  listOrgs, createOrg, updateOrg,
} from '../services/api';
import toast from 'react-hot-toast';

const fmt = (v,d=2) => v==null?'—':parseFloat(v).toLocaleString('en-US',{minimumFractionDigits:d,maximumFractionDigits:d});

/* ─── Shared Modal ─────────────────────────────────────────── */
function Modal({ title, onClose, children, footer }) {
  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3 style={{ fontSize:15.5, fontWeight:700, color:'var(--text)' }}>{title}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'var(--text2)', lineHeight:1 }}>×</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

/* ─── PAGE HEADER ─────────────────────────────────────────── */
function PageHeader({ title, subtitle, action }) {
  return (
    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:22 }}>
      <div><h1 className="page-title">{title}</h1>{subtitle && <p className="page-sub">{subtitle}</p>}</div>
      {action}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   USERS
══════════════════════════════════════════════════════════════ */
export function UsersPage() {
  const [users,  setUsers]  = useState([]);
  const [orgs,   setOrgs]   = useState([]);
  const [modal,  setModal]  = useState(null);
  const [form,   setForm]   = useState({ name:'', email:'', password:'', role:'user', org_id:'' });

  const load = async () => {
    try {
      const [ur, or] = await Promise.all([getUsers(), listOrgs()]);
      setUsers(ur.data);
      setOrgs(or.data);
    } catch {}
  };
  useEffect(()=>{ load(); },[]);

  const orgById = (id) => orgs.find(o => o.id === id);

  const save = async () => {
    try {
      const payload = { ...form, org_id: form.org_id === '' ? null : parseInt(form.org_id) };
      if (modal==='create') await createUser(payload);
      else await updateUser(payload.id, payload);
      toast.success(modal==='create'?'User created':'User updated');
      setModal(null); load();
    } catch(e) { toast.error(e.response?.data?.message||'Error'); }
  };

  const deactivate = async id => {
    if (!window.confirm('Deactivate user?')) return;
    try { await deleteUser(id); toast.success('Deactivated'); load(); } catch { toast.error('Error'); }
  };

  return (
    <div>
      <PageHeader title="User Management" subtitle="Create, edit and manage platform users. Assign users to organisations."
        action={<button className="btn btn-primary" onClick={() => { setForm({name:'',email:'',password:'',role:'user',org_id:''}); setModal('create'); }}>
          <i className="ti ti-plus" aria-hidden="true"/> Add User
        </button>} />
      <div className="card">
        <div className="table-responsive"><table className="table">
          <thead><tr><th>Name</th><th>Email</th><th>Organisation</th><th>Role</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>{users.map(u=>(
            <tr key={u.id}>
              <td style={{ fontWeight:500 }}>{u.name}</td>
              <td style={{ color:'var(--text2)', fontSize:13 }}>{u.email}</td>
              <td>
                {u.org_id
                  ? <span className="badge badge-sky">{orgById(u.org_id)?.name || `Org #${u.org_id}`}</span>
                  : <span style={{color:'var(--muted)',fontSize:12}}>Internal</span>
                }
              </td>
              <td><span className={`badge ${u.role==='admin'?'badge-blue':'badge-green'}`}>{u.role}</span></td>
              <td><span className={`badge ${u.is_active?'badge-green':'badge-red'}`}>{u.is_active?'Active':'Inactive'}</span></td>
              <td><div className="flex gap-2">
                <button className="btn btn-sm btn-outline" onClick={() => { setForm({...u, password:'', org_id: u.org_id ?? ''}); setModal('edit'); }}>
                  <i className="ti ti-edit" aria-hidden="true"/> Edit
                </button>
                <button className="btn btn-sm btn-danger" onClick={()=>deactivate(u.id)}>Deactivate</button>
              </div></td>
            </tr>
          ))}</tbody>
        </table></div>
      </div>
      {modal && (
        <Modal title={modal==='create'?'Add User':'Edit User'} onClose={()=>setModal(null)}
          footer={<><button className="btn btn-outline" onClick={()=>setModal(null)}>Cancel</button><button className="btn btn-primary" onClick={save}>Save</button></>}>
          <div className="grid2">
            <div className="form-group"><label className="form-label">Name</label><input className="form-control" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></div>
            <div className="form-group"><label className="form-label">Email</label><input className="form-control" type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} /></div>
          </div>
          <div className="grid2">
            <div className="form-group"><label className="form-label">Password {modal==='edit'?'(blank = no change)':''}</label><input className="form-control" type="password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} /></div>
            <div className="form-group"><label className="form-label">Role</label>
              <select className="form-control" value={form.role} onChange={e=>setForm({...form,role:e.target.value})}>
                <option value="user">User</option><option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Organisation</label>
            <select className="form-control" value={form.org_id} onChange={e=>setForm({...form, org_id:e.target.value})}>
              <option value="">— None (Quoteology internal) —</option>
              {orgs.map(o => (
                <option key={o.id} value={o.id}>{o.name} ({o.plan})</option>
              ))}
            </select>
          </div>
          {modal==='edit' && <div className="form-group"><label className="form-label">Status</label>
            <select className="form-control" value={form.is_active?'1':'0'} onChange={e=>setForm({...form,is_active:e.target.value==='1'})}>
              <option value="1">Active</option><option value="0">Inactive</option>
            </select>
          </div>}
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   CURRENCIES
══════════════════════════════════════════════════════════════ */
export function CurrenciesPage() {
  const [currencies, setCurrencies] = useState([]);
  const [logs,       setLogs]       = useState([]);
  const [editId,     setEditId]     = useState(null);
  const [editVals,   setEditVals]   = useState({});
  const [search,     setSearch]     = useState('');
  const [tab,        setTab]        = useState('rates');

  const load = async () => {
    getCurrencies().then(r=>setCurrencies(r.data)).catch(()=>{});
    getCurrencyLogs().then(r=>setLogs(r.data)).catch(()=>{});
  };
  useEffect(()=>{ load(); },[]);

  const startEdit = c => { setEditId(c.id); setEditVals({ units_per_usd:c.units_per_usd, usd_per_unit:c.usd_per_unit }); };

  const saveEdit = async id => {
    try {
      await updateCurrency(id, editVals);
      toast.success('Rate updated'); setEditId(null); load();
    } catch { toast.error('Error'); }
  };

  const filtered = currencies.filter(c =>
    c.code.toLowerCase().includes(search.toLowerCase()) ||
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <PageHeader title="💱 Currency Rates" subtitle="Update exchange rates — all values are units per 1 USD"
        action={<div className="flex gap-2">
          <button className={`btn ${tab==='rates'?'btn-primary':'btn-outline'}`} onClick={()=>setTab('rates')}>Rates</button>
          <button className={`btn ${tab==='logs'?'btn-primary':'btn-outline'}`} onClick={()=>setTab('logs')}>Change Log</button>
        </div>} />

      {tab==='rates' ? <>
        <div style={{ marginBottom:14 }}>
          <input className="form-control" placeholder="Search currency code or name…" value={search} onChange={e=>setSearch(e.target.value)} style={{ maxWidth:300 }} />
        </div>
        <div className="card">
          <div className="table-responsive"><table className="table">
            <thead><tr><th>Code</th><th>Name</th><th>Units / USD</th><th>USD / Unit</th><th>Last Updated</th><th>Actions</th></tr></thead>
            <tbody>{filtered.map(c=>(
              <tr key={c.id}>
                <td><span className="badge badge-blue">{c.code}</span></td>
                <td>{c.name}</td>
                <td>
                  {editId===c.id
                    ? <input className="form-control" type="number" step="0.0000001" value={editVals.units_per_usd}
                        onChange={e=>{ const v=parseFloat(e.target.value)||1; setEditVals({units_per_usd:v,usd_per_unit:(1/v)}); }}
                        style={{ width:140 }} />
                    : <span style={{ fontFamily:'monospace' }}>{fmt(c.units_per_usd,6)}</span>}
                </td>
                <td>
                  {editId===c.id
                    ? <span style={{ fontFamily:'monospace', color:'var(--text2)' }}>{fmt(editVals.usd_per_unit,8)}</span>
                    : <span style={{ fontFamily:'monospace' }}>{fmt(c.usd_per_unit,8)}</span>}
                </td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{new Date(c.updatedAt).toLocaleString('en-GB')}</td>
                <td>
                  {editId===c.id
                    ? <div className="flex gap-2">
                        <button className="btn btn-sm btn-success" onClick={()=>saveEdit(c.id)}>Save</button>
                        <button className="btn btn-sm btn-outline" onClick={()=>setEditId(null)}>Cancel</button>
                      </div>
                    : <button className="btn btn-sm btn-outline" onClick={()=>startEdit(c)}>Edit</button>}
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>
      </> : (
        <div className="card">
          <div className="table-responsive"><table className="table">
            <thead><tr><th>Currency</th><th>Old Rate</th><th>New Rate</th><th>Updated By</th><th>Date</th></tr></thead>
            <tbody>{logs.map(l=>(
              <tr key={l.id}>
                <td><span className="badge badge-blue">{l.currency_code}</span></td>
                <td style={{ fontFamily:'monospace' }}>{fmt(l.old_units_per_usd,6)}</td>
                <td style={{ fontFamily:'monospace' }}>{fmt(l.new_units_per_usd,6)}</td>
                <td>{l.updated_by_name}</td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{new Date(l.createdAt).toLocaleString('en-GB')}</td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   INSURANCE
══════════════════════════════════════════════════════════════ */
export function InsurancePage() {
  const [plans,  setPlans]  = useState([]);
  const [modal,  setModal]  = useState(null);
  const [form,   setForm]   = useState({ name:'', amount:'', currency_code:'GBP', frequency:'Per Year' });
  const [search, setSearch] = useState('');

  const load = () => getInsurance().then(r=>setPlans(r.data)).catch(()=>{});
  useEffect(()=>{ load(); },[]);

  const save = async () => {
    try {
      if (modal==='create') await createInsurance(form); else await updateInsurance(form.id,form);
      toast.success(modal==='create'?'Plan created':'Plan updated'); setModal(null); load();
    } catch(e){ toast.error(e.response?.data?.message||'Error'); }
  };

  const remove = async id => {
    if (!window.confirm('Delete this plan?')) return;
    try { await deleteInsurance(id); toast.success('Deleted'); load(); } catch { toast.error('Error'); }
  };

  const filtered = plans.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <PageHeader title="🏥 Medical Insurance" subtitle="Manage all insurance plans used in calculations"
        action={<button className="btn btn-primary" onClick={()=>{ setForm({name:'',amount:'',currency_code:'GBP',frequency:'Per Year'}); setModal('create'); }}>+ Add Plan</button>} />
      <div style={{ marginBottom:14 }}>
        <input className="form-control" placeholder="Search plans…" value={search} onChange={e=>setSearch(e.target.value)} style={{ maxWidth:320 }} />
      </div>
      <div className="card">
        <div className="table-responsive"><table className="table">
          <thead><tr><th>Plan Name</th><th>Amount</th><th>Currency</th><th>Frequency</th><th>Actions</th></tr></thead>
          <tbody>{filtered.map(p=>(
            <tr key={p.id}>
              <td style={{ fontWeight:500 }}>{p.name}</td>
              <td style={{ fontFamily:'monospace' }}>{fmt(p.amount)}</td>
              <td><span className="badge badge-blue">{p.currency_code}</span></td>
              <td><span className="badge badge-gray">{p.frequency}</span></td>
              <td><div className="flex gap-2">
                <button className="btn btn-sm btn-outline" onClick={()=>{ setForm({...p}); setModal('edit'); }}>Edit</button>
                <button className="btn btn-sm btn-danger" onClick={()=>remove(p.id)}>Delete</button>
              </div></td>
            </tr>
          ))}</tbody>
        </table></div>
      </div>
      {modal && (
        <Modal title={modal==='create'?'Add Plan':'Edit Plan'} onClose={()=>setModal(null)}
          footer={<><button className="btn btn-outline" onClick={()=>setModal(null)}>Cancel</button><button className="btn btn-primary" onClick={save}>Save</button></>}>
          <div className="form-group"><label className="form-label">Plan Name</label><input className="form-control" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></div>
          <div className="grid2">
            <div className="form-group"><label className="form-label">Amount</label><input className="form-control" type="number" step="0.01" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} /></div>
            <div className="form-group"><label className="form-label">Currency</label>
              <select className="form-control" value={form.currency_code} onChange={e=>setForm({...form,currency_code:e.target.value})}>
                {['GBP','USD','EUR','SAR','AED','INR','QAR','OMR','JPY','CNY','NOK'].map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group"><label className="form-label">Frequency</label>
            <select className="form-control" value={form.frequency} onChange={e=>setForm({...form,frequency:e.target.value})}>
              <option>Per Year</option><option>Per Month</option>
            </select>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   COUNTRY CONFIG
══════════════════════════════════════════════════════════════ */
// ── TAX SLAB TABLE ──────────────────────────────────────────────────────────
// Generic configurable slab table. Renders a clean human-readable slab grid
// from CountryConfig rows that follow the naming convention:
//   {prefix}_slab{n}_limit   → upper bound of slab n (number, in currencyUnit)
//   {prefix}_rate{n}         → tax rate for slab n (percentage, stored as decimal)
// The last slab (index = slabCount) has no limit key — it's always open-ended.
//
// Surcharge tiers follow the same pattern but with different key names:
//   {prefix}_t{n}_limit      → upper bound of tier n
//   {prefix}_rate{n}         → surcharge rate for tier n
//
// Used by: India New Tax Regime (ntr_*)
// Will be reused when Actual Tax is added for other countries.

function TaxSlabTable({ title, rows, prefix, slabCount, limitKeySuffix = '_slab{n}_limit', rateKeySuffix = '_rate{n}', currencySymbol = '', currencyUnit = '', onSaved }) {
  const [editIdx,    setEditIdx]    = useState(null);
  const [editLimit,  setEditLimit]  = useState('');
  const [editRate,   setEditRate]   = useState('');

  // Build slab array from config rows
  const getVal = (key) => {
    const row = rows.find(r => r.param_key === key);
    return row ? { id: row.id, value: row.param_value, type: row.param_type } : null;
  };

  const slabs = [];
  for (let n = 1; n <= slabCount; n++) {
    const limitKey = prefix + limitKeySuffix.replace('{n}', n);
    const rateKey  = prefix + rateKeySuffix.replace('{n}', n);
    const limitRow = getVal(limitKey);
    const rateRow  = getVal(rateKey);
    if (!rateRow) continue;
    slabs.push({ n, limitRow, rateRow, isTop: n === slabCount });
  }

  const fmt = (val) => {
    const n = parseFloat(val);
    if (isNaN(n)) return val;
    return currencyUnit === 'INR'
      ? '₹' + n.toLocaleString('en-IN')
      : currencySymbol + n.toLocaleString();
  };

  const openEdit = (n) => {
    const s = slabs[n];
    setEditIdx(n);
    setEditLimit(s.limitRow ? s.limitRow.value : '');
    setEditRate(s.rateRow ? String(parseFloat(s.rateRow.value) * 100) : '');
  };

  const saveRow = async (n) => {
    const s = slabs[n];
    try {
      const promises = [];
      if (s.limitRow && !s.isTop) {
        promises.push(updateConfig(s.limitRow.id, { param_value: editLimit }));
      }
      if (s.rateRow) {
        promises.push(updateConfig(s.rateRow.id, { param_value: String(parseFloat(editRate) / 100) }));
      }
      await Promise.all(promises);
      toast.success('Slab updated');
      setEditIdx(null);
      if (onSaved) onSaved();
    } catch { toast.error('Error saving slab'); }
  };

  if (slabs.length === 0) return null;

  return (
    <div className="card" style={{ marginTop:18 }}>
      <div className="card-header">
        <span style={{ fontWeight:600, color:'var(--text)' }}>{title}</span>
      </div>
      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width:50 }}>Slab</th>
              <th>Income Range</th>
              <th style={{ width:160 }}>Rate (%)</th>
              <th style={{ width:140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {slabs.map((s, i) => {
              const from = i === 0 ? 0 : parseFloat(slabs[i-1].limitRow?.value || 0) + 1;
              const to   = s.limitRow ? parseFloat(s.limitRow.value) : null;
              const rate = parseFloat(s.rateRow?.value || 0) * 100;
              const isEditing = editIdx === i;

              return (
                <tr key={s.n}>
                  <td style={{ fontWeight:600, color:'var(--text2)', textAlign:'center' }}>{s.n}</td>
                  <td>
                    {isEditing && !s.isTop ? (
                      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <span style={{ color:'var(--text2)', fontSize:13 }}>{fmt(from)} – </span>
                        <input
                          className="form-control"
                          type="number"
                          value={editLimit}
                          onChange={e => setEditLimit(e.target.value)}
                          style={{ width:140 }}
                          placeholder="Upper limit"
                        />
                      </div>
                    ) : (
                      <span style={{ fontFamily:'monospace', fontSize:13 }}>
                        {fmt(from)}
                        {to !== null ? ` – ${fmt(to)}` : ' and above'}
                      </span>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        className="form-control"
                        type="number"
                        step="0.01"
                        value={editRate}
                        onChange={e => setEditRate(e.target.value)}
                        style={{ width:100 }}
                        placeholder="%"
                      />
                    ) : (
                      <span style={{ fontFamily:'monospace', fontWeight:500, color:'var(--text)' }}>
                        {rate.toFixed(2)}%
                      </span>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <div className="flex gap-2">
                        <button className="btn btn-sm btn-success" onClick={() => saveRow(i)}>Save</button>
                        <button className="btn btn-sm btn-outline" onClick={() => setEditIdx(null)}>Cancel</button>
                      </div>
                    ) : (
                      <button className="btn btn-sm btn-outline" onClick={() => openEdit(i)}>Edit</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Slab table definitions — one entry per country/regime that uses slabs.
// Add new entries here when implementing Actual Tax for other countries.
const TAX_SLAB_CONFIGS = {
  'China': [
    {
      title: 'Individual Income Tax (IIT) Brackets — Per Day CNY',
      prefix: 'cn_iit',
      slabCount: 7,
      limitKeySuffix: '_slab{n}_limit',
      rateKeySuffix: '_rate{n}',
      currencyUnit: 'CNY',
      filterGroup: 'Tax - IIT Brackets',
    },
  ],
  'Norway': [
    {
      title: 'Trinnskatt (Step Tax) Brackets — 2025',
      prefix: 'no_trinnskatt',
      slabCount: 5,
      limitKeySuffix: '_slab{n}_limit',
      rateKeySuffix: '_rate{n}',
      currencyUnit: 'NOK',
      filterGroup: 'Tax - Trinnskatt',
    },
  ],
  'Japan': [
    {
      title: 'National Income Tax Brackets — 2025 (Residents)',
      prefix: 'jp_it',
      slabCount: 7,
      limitKeySuffix: '_slab{n}_limit',
      rateKeySuffix: '_rate{n}',
      currencyUnit: 'JPY',
      filterGroup: 'Tax - National IT Brackets',
    },
  ],
  'Netherlands': [
    {
      title: 'Box 1 Income Tax Brackets — 2025',
      prefix: 'nl_box1',
      slabCount: 3,
      limitKeySuffix: '_slab{n}_limit',
      rateKeySuffix: '_rate{n}',
      currencyUnit: 'EUR',
      filterGroup: 'Tax - Box 1 Brackets',
    },
  ],
  'Australia': [
    {
      title: 'Resident Income Tax Brackets — ATO 2024-25 / 2025-26',
      prefix: 'ato_resident',
      slabCount: 5,
      limitKeySuffix: '_slab{n}_limit',
      rateKeySuffix: '_rate{n}',
      currencyUnit: 'AUD',
      filterGroup: 'Tax - ATO Brackets',
    },
    {
      title: 'Non-Resident Income Tax Brackets — ATO 2024-25 / 2025-26',
      prefix: 'ato_nonres',
      slabCount: 4,
      limitKeySuffix: '_slab{n}_limit',
      rateKeySuffix: '_rate{n}',
      currencyUnit: 'AUD',
      filterGroup: null,
    },
  ],
  'United States': [
    {
      title: 'Federal Income Tax Brackets — 2025 Single Filer',
      prefix: 'fed',
      slabCount: 7,
      limitKeySuffix: '_slab{n}_limit',
      rateKeySuffix: '_rate{n}',
      currencyUnit: 'USD',
      filterGroup: 'Tax - Federal Brackets',
    },
  ],
  'India': [
    {
      title: 'New Tax Regime — Income Tax Slabs (FY 2025-26)',
      prefix: 'ntr',
      slabCount: 7,
      limitKeySuffix: '_slab{n}_limit',
      rateKeySuffix: '_rate{n}',
      currencyUnit: 'INR',
      filterGroup: 'Tax - New Regime',
    },
    {
      title: 'New Tax Regime — Surcharge Tiers',
      prefix: 'ntr_surcharge',
      slabCount: 4,
      limitKeySuffix: '_t{n}_limit',
      rateKeySuffix: '_rate{n}',
      currencyUnit: 'INR',
      filterGroup: null, // same group, already filtered by prefix
    },
  ],
};

export function CountryConfigPage() {
  const [configs,        setConfigs]        = useState({});
  const [activeCountry,  setActiveCountry]  = useState('Saudi Arabia');
  const [editId,         setEditId]         = useState(null);
  const [editVal,        setEditVal]        = useState('');

  const load = () => getAllConfigs().then(r=>setConfigs(r.data)).catch(()=>{});
  useEffect(()=>{ load(); },[]);

  const saveEdit = async (id, paramType) => {
    try {
      const val = paramType === 'percentage' ? String(parseFloat(editVal) / 100) : editVal;
      await updateConfig(id, { param_value: val });
      toast.success('Saved'); setEditId(null); load();
    }
    catch { toast.error('Error'); }
  };

  const countryRows = configs[activeCountry] || [];
  const slabConfigs = TAX_SLAB_CONFIGS[activeCountry] || [];

  // Groups to show in the main table — exclude slab groups that have a dedicated table
  const slabGroups = new Set(slabConfigs.filter(s=>s.filterGroup).map(s=>s.filterGroup));
  const groups = [...new Set(countryRows.map(c=>c.param_group))]
    .filter(g => !slabGroups.has(g))
    .sort();

  return (
    <div>
      <div style={{ marginBottom:22 }}>
        <h1 className="page-title">⚙️ Country Configuration</h1>
        <p className="page-sub">Edit tax rates, fees and parameters used in the calculation engine</p>
      </div>

      <div className="flex gap-2 mb-4">
        {['Saudi Arabia','India','Australia','United States','Netherlands','Japan','Norway','China'].map(c=>(
          <button key={c} className={`btn ${activeCountry===c?'btn-primary':'btn-outline'}`} onClick={()=>setActiveCountry(c)}>
            {c==='Saudi Arabia'?'🇸🇦':c==='India'?'🇮🇳':c==='Australia'?'🇦🇺':c==='United States'?'🇺🇸':c==='Netherlands'?'🇳🇱':c==='Japan'?'🇯🇵':c==='Norway'?'🇳🇴':'🇨🇳'} {c}
          </button>
        ))}
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
        {groups.map(group=>(
          <div className="card" key={group}>
            <div className="card-header"><span style={{ fontWeight:600, color:'var(--text)' }}>{group}</span></div>
            <div className="table-responsive"><table className="table">
              <thead><tr><th>Parameter</th><th>Value</th><th>Type</th><th style={{ width:140 }}>Actions</th></tr></thead>
              <tbody>
                {countryRows.filter(c=>c.param_group===group).map(cfg=>(
                  <tr key={cfg.id}>
                    <td style={{ fontWeight:500 }}>{cfg.param_label}</td>
                    <td>
                      {editId===cfg.id
                        ? <input className="form-control" value={editVal} onChange={e=>setEditVal(e.target.value)} style={{ width:180 }} />
                        : <span style={{ fontFamily:'monospace', fontWeight:500, color:'var(--text)' }}>
                            {cfg.param_type==='percentage'
                              ? `${(parseFloat(cfg.param_value)*100).toFixed(3)}%`
                              : cfg.param_value}
                          </span>}
                    </td>
                    <td><span className="badge badge-gray">{cfg.param_type}</span></td>
                    <td>
                      {editId===cfg.id
                        ? <div className="flex gap-2">
                            <button className="btn btn-sm btn-success" onClick={()=>saveEdit(cfg.id, cfg.param_type)}>Save</button>
                            <button className="btn btn-sm btn-outline" onClick={()=>setEditId(null)}>Cancel</button>
                          </div>
                        : <button className="btn btn-sm btn-outline" onClick={()=>{ setEditId(cfg.id); setEditVal(cfg.param_type==='percentage' ? String(parseFloat(cfg.param_value)*100) : cfg.param_value); }}>Edit</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        ))}

        {/* Tax Slab Tables — rendered below the main config groups */}
        {slabConfigs.map((sc, i) => (
          <TaxSlabTable
            key={i}
            title={sc.title}
            rows={countryRows}
            prefix={sc.prefix}
            slabCount={sc.slabCount}
            limitKeySuffix={sc.limitKeySuffix}
            rateKeySuffix={sc.rateKeySuffix}
            currencyUnit={sc.currencyUnit}
            onSaved={load}
          />
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   CUSTOM COUNTRY BURDENS
══════════════════════════════════════════════════════════════ */
const CB_COUNTRIES = ['Saudi Arabia','India','Australia','United States','Netherlands','Japan','Norway','China'];
const CB_COUNTRY_FLAG = { 'Saudi Arabia':'🇸🇦','India':'🇮🇳','Australia':'🇦🇺','United States':'🇺🇸','Netherlands':'🇳🇱','Japan':'🇯🇵','Norway':'🇳🇴','China':'🇨🇳' };
const CB_COUNTRY_CURRENCY = { 'Saudi Arabia':'SAR','India':'INR','Australia':'AUD','United States':'USD','Netherlands':'EUR','Japan':'JPY','Norway':'NOK','China':'CNY' };
const CB_CURRENCIES = ['USD','EUR','GBP','SAR','AED','INR','QAR','OMR','JPY','CNY','NOK','AUD'];
const CB_FREQUENCIES = ['Per Day','Per Month','Per Year'];

// Minimal valid inputs per country, just enough to get back a real breakdown
// so the Base dropdown can be populated with that country's actual current
// burden line item labels (kept in sync with the engine automatically).
const CB_PROBE_INPUTS = {
  'Saudi Arabia':   { billRate: 1000, payFrequency: 'Per Day', rateType: 'gross', outputCurrency: 'SAR', medicalInsuranceName: 'None', contractorType: 'Expat' },
  'India':          { billRate: 1000, payFrequency: 'Per Day', rateType: 'gross', outputCurrency: 'INR', medicalInsuranceName: 'None', contractorType: 'Expat' },
  'Australia':      { billRate: 1000, payFrequency: 'Per Day', rateType: 'gross', outputCurrency: 'AUD', medicalInsuranceName: 'None', contractorType: 'Expat', stateTerritory: 'New South Wales' },
  'United States':  { billRate: 1000, payFrequency: 'Per Day', rateType: 'gross', outputCurrency: 'USD', medicalInsuranceName: 'None', contractorType: 'Expat', usStateEntity: 'AL - NES GLOBAL LLC' },
  'Netherlands':    { billRate: 1000, payFrequency: 'Per Day', rateType: 'gross', outputCurrency: 'EUR', medicalInsuranceName: 'None', contractorType: 'Expat' },
  'Japan':          { billRate: 1000, payFrequency: 'Per Day', rateType: 'gross', outputCurrency: 'JPY', medicalInsuranceName: 'None', contractorType: 'Expat' },
  'Norway':         { billRate: 1000, payFrequency: 'Per Day', rateType: 'gross', outputCurrency: 'NOK', medicalInsuranceName: 'None', contractorType: 'Local' },
  'China':          { billRate: 1000, payFrequency: 'Per Day', rateType: 'gross', outputCurrency: 'CNY', medicalInsuranceName: 'None', contractorType: 'Expat', chinaCity: 'Beijing' },
};

const CB_EMPTY_FORM = {
  country: 'Saudi Arabia', label: '', is_active: false, calc_type: 'percentage',
  base_line_item: 'grossRateToMan', rate_pct: '', flat_amount: '',
  currency_code: 'SAR', frequency: 'Per Month',
  apply_cap: false, cap_amount: '', cap_currency_code: 'SAR', cap_frequency: 'Per Month',
};

export function CustomBurdensPage() {
  const [burdens,       setBurdens]       = useState({});
  const [activeCountry, setActiveCountry] = useState('Saudi Arabia');
  const [modal,         setModal]         = useState(null);
  const [form,          setForm]          = useState(CB_EMPTY_FORM);
  const [baseOptions,   setBaseOptions]   = useState(['grossRateToMan']);
  const [baseLabels,    setBaseLabels]    = useState({ grossRateToMan: 'Gross Rate to Man' });
  const [loadingBases,  setLoadingBases]  = useState(false);

  const load = () => getAllCustomBurdens().then(r => setBurdens(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  // Whenever the form's country changes, fetch a live breakdown for that
  // country so the Base dropdown reflects its actual current burden items.
  const loadBaseOptions = async (country) => {
    setLoadingBases(true);
    try {
      const probe = CB_PROBE_INPUTS[country];
      const res = await calculate({ country, inputs: probe });
      const items = res.data?.breakdown?.burdens?.items || [];
      const labels = { grossRateToMan: 'Gross Rate to Man' };
      const opts = ['grossRateToMan'];
      items.forEach(i => { if (!i.isCustom) { opts.push(i.label); labels[i.label] = i.label; } });
      setBaseOptions(opts);
      setBaseLabels(labels);
    } catch {
      setBaseOptions(['grossRateToMan']);
      setBaseLabels({ grossRateToMan: 'Gross Rate to Man' });
    } finally {
      setLoadingBases(false);
    }
  };

  const openCreate = () => {
    const country = activeCountry;
    setForm({ ...CB_EMPTY_FORM, country, currency_code: CB_COUNTRY_CURRENCY[country], cap_currency_code: CB_COUNTRY_CURRENCY[country] });
    setModal('create');
    loadBaseOptions(country);
  };

  const openEdit = (b) => {
    setForm({ ...b, rate_pct: b.rate_pct != null ? parseFloat(b.rate_pct) * 100 : '', flat_amount: b.flat_amount ?? '', cap_amount: b.cap_amount ?? '' });
    setModal('edit');
    loadBaseOptions(b.country);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        rate_pct:    form.calc_type === 'percentage' ? (parseFloat(form.rate_pct) || 0) / 100 : null,
        flat_amount: form.calc_type === 'flat'       ? parseFloat(form.flat_amount) || 0 : null,
        base_line_item: form.calc_type === 'percentage' ? form.base_line_item : null,
        cap_amount: form.apply_cap ? (parseFloat(form.cap_amount) || 0) : null,
        cap_currency_code: form.apply_cap ? form.cap_currency_code : null,
        cap_frequency: form.apply_cap ? form.cap_frequency : null,
      };
      if (modal === 'create') await createCustomBurden(payload);
      else await updateCustomBurden(form.id, payload);
      toast.success(modal === 'create' ? 'Burden created' : 'Burden updated');
      setActiveCountry(form.country);
      setModal(null);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Error'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this custom burden?')) return;
    try { await deleteCustomBurden(id); toast.success('Deleted'); load(); }
    catch { toast.error('Error'); }
  };

  const countryRows = burdens[activeCountry] || [];

  return (
    <div>
      <PageHeader title="➕ Custom Country Burdens" subtitle="Add admin-defined burdens that apply on top of each country's standard calculation"
        action={<button className="btn btn-primary" onClick={openCreate}>+ Add Burden</button>} />

      <div className="flex gap-2 mb-4">
        {CB_COUNTRIES.map(c => (
          <button key={c} className={`btn ${activeCountry===c?'btn-primary':'btn-outline'}`} onClick={()=>setActiveCountry(c)}>
            {CB_COUNTRY_FLAG[c]} {c}
          </button>
        ))}
      </div>

      <div className="card">
        <div className="table-responsive"><table className="table">
          <thead><tr><th>Label</th><th>Type</th><th>Basis</th><th>Cap</th><th>Active</th><th style={{ width:160 }}>Actions</th></tr></thead>
          <tbody>
            {countryRows.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign:'center', color:'var(--text2)', padding:'24px 0' }}>No custom burdens for {activeCountry} yet.</td></tr>
            )}
            {countryRows.map(b => (
              <tr key={b.id}>
                <td style={{ fontWeight:500 }}>{b.label}</td>
                <td><span className="badge badge-gray">{b.calc_type === 'percentage' ? 'Percentage' : 'Flat Amount'}</span></td>
                <td style={{ fontFamily:'monospace', fontSize:13 }}>
                  {b.calc_type === 'percentage'
                    ? `${(parseFloat(b.rate_pct)*100).toFixed(3)}% of ${b.base_line_item === 'grossRateToMan' ? 'Gross Rate to Man' : b.base_line_item}`
                    : `${parseFloat(b.flat_amount).toLocaleString()} ${b.currency_code} / ${b.frequency.replace('Per ','')}`}
                </td>
                <td style={{ fontFamily:'monospace', fontSize:13 }}>
                  {b.apply_cap ? `${parseFloat(b.cap_amount).toLocaleString()} ${b.cap_currency_code} / ${b.cap_frequency.replace('Per ','')}` : '—'}
                </td>
                <td>{b.is_active ? <span className="badge badge-green">Active</span> : <span className="badge badge-gray">Inactive</span>}</td>
                <td><div className="flex gap-2">
                  <button className="btn btn-sm btn-outline" onClick={()=>openEdit(b)}>Edit</button>
                  <button className="btn btn-sm btn-danger" onClick={()=>remove(b.id)}>Delete</button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {modal && (
        <Modal title={modal==='create' ? 'Add Custom Burden' : 'Edit Custom Burden'} onClose={()=>setModal(null)}
          footer={<><button className="btn btn-outline" onClick={()=>setModal(null)}>Cancel</button><button className="btn btn-primary" onClick={save}>Save</button></>}>

          <div className="grid2">
            <div className="form-group">
              <label className="form-label">Country</label>
              <select className="form-control" value={form.country}
                onChange={e => {
                  const country = e.target.value;
                  setForm({ ...form, country, currency_code: CB_COUNTRY_CURRENCY[country], cap_currency_code: CB_COUNTRY_CURRENCY[country], base_line_item: 'grossRateToMan' });
                  loadBaseOptions(country);
                }}>
                {CB_COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" style={{ display:'flex', alignItems:'center', gap:8 }}>
                <input type="checkbox" checked={!!form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
                <span>Active</span>
              </label>
              <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Applies to calculations only when checked</div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Label</label>
            <input className="form-control" placeholder="e.g. Regional Training Levy" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} />
          </div>

          <div className="form-group">
            <label className="form-label">Calculation Type</label>
            <select className="form-control" value={form.calc_type} onChange={e => setForm({ ...form, calc_type: e.target.value })}>
              <option value="percentage">Percentage of a line item</option>
              <option value="flat">Flat Amount</option>
            </select>
          </div>

          {form.calc_type === 'percentage' ? (
            <div className="grid2">
              <div className="form-group">
                <label className="form-label">Base (% of)</label>
                <select className="form-control" value={form.base_line_item} disabled={loadingBases}
                  onChange={e => setForm({ ...form, base_line_item: e.target.value })}>
                  {baseOptions.map(opt => <option key={opt} value={opt}>{baseLabels[opt] || opt}</option>)}
                </select>
                {loadingBases && <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Loading current line items…</div>}
              </div>
              <div className="form-group">
                <label className="form-label">Rate (%)</label>
                <input className="form-control" type="number" step="0.001" placeholder="e.g. 5" value={form.rate_pct}
                  onChange={e => setForm({ ...form, rate_pct: e.target.value })} />
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">Flat Amount</label>
              <input className="form-control" type="number" step="0.01" value={form.flat_amount}
                onChange={e => setForm({ ...form, flat_amount: e.target.value })} />
            </div>
          )}

          <div className="grid2">
            <div className="form-group">
              <label className="form-label">Currency</label>
              <select className="form-control" value={form.currency_code} onChange={e => setForm({ ...form, currency_code: e.target.value })}>
                {CB_CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
              {form.calc_type === 'percentage' && (
                <div style={{ fontSize:11.5, color:'var(--muted)', marginTop:4 }}>Used for the cap only — the rate applies directly to the base line item's value</div>
              )}
            </div>
            {form.calc_type === 'flat' && (
              <div className="form-group">
                <label className="form-label">Frequency</label>
                <select className="form-control" value={form.frequency} onChange={e => setForm({ ...form, frequency: e.target.value })}>
                  {CB_FREQUENCIES.map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display:'flex', alignItems:'center', gap:8 }}>
              <input type="checkbox" checked={!!form.apply_cap} onChange={e => setForm({ ...form, apply_cap: e.target.checked })} />
              <span>Apply Cap</span>
            </label>
          </div>

          {form.apply_cap && (
            <div className="grid2">
              <div className="form-group">
                <label className="form-label">Cap Amount</label>
                <input className="form-control" type="number" step="0.01" value={form.cap_amount}
                  onChange={e => setForm({ ...form, cap_amount: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Cap Currency</label>
                <select className="form-control" value={form.cap_currency_code} onChange={e => setForm({ ...form, cap_currency_code: e.target.value })}>
                  {CB_CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Cap Frequency</label>
                <select className="form-control" value={form.cap_frequency} onChange={e => setForm({ ...form, cap_frequency: e.target.value })}>
                  {CB_FREQUENCIES.map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   ORGANISATIONS
══════════════════════════════════════════════════════════════ */
const ALL_COUNTRIES = ['Saudi Arabia','India','Australia','United States','Netherlands','Japan','Norway','China'];
const PLAN_LABELS   = { essential:'Essential', standard:'Standard', enterprise:'Enterprise' };
const STATUS_LABELS = { active:'Active', suspended:'Suspended', cancelled:'Cancelled' };
const PLAN_COLORS   = { essential:'badge-sky', standard:'badge-blue', enterprise:'badge-purple' };
const STATUS_COLORS = { active:'badge-green', suspended:'badge-yellow', cancelled:'badge-red' };

const DEFAULT_ORG_FORM = {
  name:'', plan:'standard', billing_cycle:'monthly', status:'active',
  allowed_countries:['Saudi Arabia','India','Australia','United States','Netherlands','Japan','Norway','China'],
  credit_balance: 0,
  quota_used: 0,
};

export function OrganisationsPage() {
  const [orgs,    setOrgs]    = useState([]);
  const [modal,   setModal]   = useState(null); // null | 'edit' | 'create' | 'users'
  const [selected, setSelected] = useState(null);
  const [form,    setForm]    = useState(DEFAULT_ORG_FORM);
  const [saving,  setSaving]  = useState(false);

  const load = () => listOrgs().then(r => setOrgs(r.data)).catch(() => toast.error('Failed to load organisations'));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(DEFAULT_ORG_FORM); setSelected(null); setModal('create'); };
  const openEdit   = (org) => {
    setSelected(org);
    setForm({
      name:               org.name,
      plan:               org.plan,
      billing_cycle:      org.billing_cycle,
      status:             org.status,
      allowed_countries:  org.allowed_countries || ALL_COUNTRIES,
      credit_balance:     org.credit_balance || 0,
      quota_used:         org.quota_used || 0,
    });
    setModal('edit');
  };
  const openUsers = (org) => { setSelected(org); setModal('users'); };

  const toggleCountry = (c) => {
    const limit = form.plan === 'essential' ? 3 : 8;
    setForm(f => {
      const sel = f.allowed_countries || [];
      if (sel.includes(c)) return { ...f, allowed_countries: sel.filter(x => x !== c) };
      if (form.plan === 'essential' && sel.length >= limit) { toast.error('Essential plan allows 3 countries'); return f; }
      return { ...f, allowed_countries: [...sel, c] };
    });
  };

  const handlePlanChange = (plan) => {
    setForm(f => ({
      ...f,
      plan,
      quota_used: 0,
      allowed_countries: plan === 'essential'
        ? (f.allowed_countries || []).slice(0, 3)
        : ALL_COUNTRIES,
    }));
  };

  const handleSave = async () => {
    if (!form.name) return toast.error('Organisation name required');
    if (form.plan === 'essential' && (form.allowed_countries || []).length !== 3) {
      return toast.error('Essential plan requires exactly 3 countries');
    }
    setSaving(true);
    try {
      if (modal === 'create') {
        await createOrg({ ...form, quota_used: parseInt(form.quota_used)||0, credit_balance: parseInt(form.credit_balance)||0 });
        toast.success('Organisation created');
      } else {
        await updateOrg(selected.id, { ...form, quota_used: parseInt(form.quota_used)||0, credit_balance: parseInt(form.credit_balance)||0 });
        toast.success('Organisation updated');
      }
      setModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const quotaPct = (org) => {
    if (!org.quota_used && !org.credit_balance) return 0;
    const limits = { essential:50, standard:200, enterprise:null };
    const limit  = limits[org.plan];
    if (!limit) return 0;
    return Math.min(100, Math.round((org.quota_used / limit) * 100));
  };

  return (
    <div>
      <PageHeader
        title="Organisations"
        subtitle="Manage customer accounts, plans, and quotation limits."
        action={
          <button className="btn btn-primary" onClick={openCreate}>
            <i className="ti ti-plus" aria-hidden="true"/> New Organisation
          </button>
        }
      />

      {/* Org table */}
      <div className="card">
        <div style={{overflowX:'auto'}}>
          <table className="table">
            <thead>
              <tr>
                <th>Organisation</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Quota used</th>
                <th>Credits</th>
                <th>Countries</th>
                <th>Users</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orgs.length === 0 && (
                <tr><td colSpan={8} style={{textAlign:'center',color:'var(--muted)',padding:'2rem'}}>No organisations yet</td></tr>
              )}
              {orgs.map(org => {
                const limits = { essential:50, standard:200, enterprise:null };
                const limit  = limits[org.plan];
                const pct    = quotaPct(org);
                return (
                  <tr key={org.id}>
                    <td>
                      <div style={{fontWeight:600,color:'var(--text)'}}>{org.name}</div>
                      <div style={{fontSize:11.5,color:'var(--muted)'}}>{org.billing_cycle}</div>
                    </td>
                    <td><span className={`badge ${PLAN_COLORS[org.plan] || 'badge-gray'}`}>{PLAN_LABELS[org.plan]}</span></td>
                    <td><span className={`badge ${STATUS_COLORS[org.status] || 'badge-gray'}`}>{STATUS_LABELS[org.status]}</span></td>
                    <td>
                      <div style={{fontSize:12.5,marginBottom:4}}>
                        {org.quota_used || 0}{limit ? ` / ${limit}` : ' / ∞'}
                      </div>
                      {limit && (
                        <div style={{background:'var(--surface-2)',borderRadius:4,height:5,width:80,overflow:'hidden'}}>
                          <div style={{
                            height:'100%',borderRadius:4,
                            background: pct >= 90 ? 'var(--danger)' : pct >= 70 ? 'var(--warning)' : 'var(--precision)',
                            width:`${pct}%`,
                          }}/>
                        </div>
                      )}
                    </td>
                    <td>
                      <span style={{fontWeight:600,color: (org.credit_balance||0) > 0 ? 'var(--success)' : 'var(--muted)'}}>
                        {org.credit_balance || 0}
                      </span>
                    </td>
                    <td style={{fontSize:12,color:'var(--text2)'}}>
                      {(org.allowed_countries || []).length === 8
                        ? 'All 8'
                        : (org.allowed_countries || []).join(', ')}
                    </td>
                    <td>
                      <button className="btn btn-ghost btn-sm" onClick={() => openUsers(org)}>
                        <i className="ti ti-users" aria-hidden="true"/> {(org.users || []).length}
                      </button>
                    </td>
                    <td>
                      <button className="btn btn-outline btn-sm" onClick={() => openEdit(org)}>
                        <i className="ti ti-edit" aria-hidden="true"/> Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit modal */}
      {(modal === 'create' || modal === 'edit') && (
        <Modal
          title={modal === 'create' ? 'New Organisation' : `Edit — ${selected?.name}`}
          onClose={() => setModal(null)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <><div className="spinner"/>Saving…</> : modal === 'create' ? 'Create' : 'Save changes'}
              </button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">Organisation name</label>
            <input className="form-control" value={form.name} onChange={e => setForm({...form, name:e.target.value})} placeholder="Acme Staffing Ltd"/>
          </div>

          <div className="grid2">
            <div className="form-group">
              <label className="form-label">Plan</label>
              <select className="form-control" value={form.plan} onChange={e => handlePlanChange(e.target.value)}>
                <option value="essential">Essential — $349/mo</option>
                <option value="standard">Standard — $499/mo</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Billing cycle</label>
              <select className="form-control" value={form.billing_cycle} onChange={e => setForm({...form, billing_cycle:e.target.value})}>
                <option value="monthly">Monthly</option>
                <option value="annual">Annual (20% off)</option>
              </select>
            </div>
          </div>

          <div className="grid2">
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control" value={form.status} onChange={e => setForm({...form, status:e.target.value})}>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Quota used this month</label>
              <input className="form-control" type="text" inputMode="numeric" pattern="[0-9]*"
                value={form.quota_used}
                onChange={e => { const v=e.target.value.replace(/[^0-9]/g,''); setForm({...form,quota_used:v}); }}
                onBlur={e => setForm({...form,quota_used:parseInt(e.target.value)||0})}
              />
              <div style={{fontSize:11,color:'var(--muted)',marginTop:4}}>Set to 0 to reset usage mid-month</div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Credit balance (top-ups)</label>
            <input className="form-control" type="text" inputMode="numeric" pattern="[0-9]*"
              value={form.credit_balance}
              onChange={e => { const v=e.target.value.replace(/[^0-9]/g,''); setForm({...form,credit_balance:v}); }}
              onBlur={e => setForm({...form,credit_balance:parseInt(e.target.value)||0})}
            />
            <div style={{fontSize:11,color:'var(--muted)',marginTop:4}}>Manually add top-up credits</div>
          </div>

          {/* Country selector */}
          <div className="form-group">
            <label className="form-label">
              Active countries
              {form.plan === 'essential' && <span style={{color:'var(--muted)',fontWeight:400,marginLeft:6}}>(select 3)</span>}
            </label>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
              {ALL_COUNTRIES.map(c => {
                const sel = (form.allowed_countries || []).includes(c);
                const disabled = form.plan !== 'essential' && sel; // can't deselect on standard+
                return (
                  <div key={c} onClick={() => !disabled && toggleCountry(c)}
                    style={{
                      display:'flex',alignItems:'center',gap:8,padding:'7px 10px',
                      border: sel ? '1.5px solid var(--precision)' : '1px solid var(--border)',
                      borderRadius:7,cursor: disabled ? 'default' : 'pointer',
                      background: sel ? 'var(--cloud)' : 'var(--surface-1)',
                      transition:'all .12s',
                    }}>
                    <div style={{
                      width:15,height:15,borderRadius:'50%',flexShrink:0,
                      background: sel ? 'var(--precision)' : 'transparent',
                      border: sel ? 'none' : '2px solid var(--border-s)',
                      display:'flex',alignItems:'center',justifyContent:'center',
                    }}>
                      {sel && <i className="ti ti-check" style={{fontSize:9,color:'#fff'}} aria-hidden="true"/>}
                    </div>
                    <span style={{fontSize:12.5,color: sel ? 'var(--precision-h)' : 'var(--text2)',fontWeight: sel ? 500 : 400}}>{c}</span>
                  </div>
                );
              })}
            </div>
            <div style={{fontSize:12,color:'var(--muted)',marginTop:6}}>
              {(form.allowed_countries||[]).length} / {form.plan === 'essential' ? 3 : 8} selected
            </div>
          </div>
        </Modal>
      )}

      {/* Users modal */}
      {modal === 'users' && selected && (
        <Modal title={`Users — ${selected.name}`} onClose={() => setModal(null)}>
          {(selected.users || []).length === 0 ? (
            <p style={{color:'var(--muted)',fontSize:13}}>No users in this organisation.</p>
          ) : (
            <table className="table">
              <thead>
                <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Last login</th></tr>
              </thead>
              <tbody>
                {(selected.users || []).map(u => (
                  <tr key={u.id}>
                    <td style={{fontWeight:500}}>{u.name}</td>
                    <td style={{fontSize:12.5}}>{u.email}</td>
                    <td><span className={`badge ${u.role === 'admin' ? 'badge-blue' : 'badge-gray'}`}>{u.role}</span></td>
                    <td><span className={`badge ${u.is_active ? 'badge-green' : 'badge-red'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                    <td style={{fontSize:12,color:'var(--muted)'}}>
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString('en-GB') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      )}
    </div>
  );
}